import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminLogin, resetAuthError } from '../store/slices/authSlice';
import { RootState } from '../store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { refreshUserInfo } from '../hooks/useUserInfo';
import { ReactComponent as PasswordEyeIcon } from '../assets/password_eye.svg';
import { ReactComponent as LoginCheckIcon } from '../assets/login-check.svg';

interface AdminLoginFormValues {
  id: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const [formValues, setFormValues] = useState<AdminLoginFormValues>({
    id: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<AdminLoginFormValues>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // 페이지 로드 시 이미 로그인된 관리자인지 체크
    const token = localStorage.getItem('access_token');
    /// console.log('관리자 로그인 토큰 확인:', {token});
    // /// console.log('AdminLogin - 페이지 로드, 토큰 존재:', !!token);
    // /// console.log('AdminLogin - isAuthenticated:', isAuthenticated);
    // /// console.log('AdminLogin - user:', user);
    // /// console.log('AdminLogin - user role:', user?.role);
    
    // 관리자 권한 체크 (대소문자 구분 없이 체크)
    const isAdmin = user?.role && (
      user.role.toLowerCase() === 'admin' || 
      user.role.toUpperCase() === 'ADMIN' ||
      user.role === 'admin' ||
      user.role === 'ADMIN'
    );
    
    // 이미 로그인된 관리자라면 바로 대시보드로 리다이렉트
    if ((isAuthenticated || token) && isAdmin) {
      /// console.log('AdminLogin - 이미 로그인된 관리자, 대시보드로 리다이렉트');
      navigate('/admin/dashboard', { replace: true });
      return;
    }else{
      /// console.log('관리자가 아님', {isAuthenticated, token, isAdmin});
    }
    
    // 토큰은 있지만 사용자 정보가 없는 경우 사용자 정보 로드
    if (token && !user && !loading) {
      // /// console.log('AdminLogin - 토큰은 있지만 사용자 정보 없음, 정보 로드 시도');
      refreshUserInfo().then(() => {
        // /// console.log('AdminLogin - 사용자 정보 로드 완료 후 다시 체크');
      });
    }
    
    // Redux 에러가 있으면 로그인 에러로 설정
    if (error) {
      // 관리자 권한이 없는 경우 alert로 표시
      if (error.includes('관리자 권한이 없습니다') || error.includes('403')) {
        alert('관리자 권한이 없습니다.');
        setLoginError('');
      } else {
        setLoginError(error);
      }
    }
    
    // 컴포넌트 마운트 시 에러 초기화
    dispatch(resetAuthError());
  }, [isAuthenticated, user, navigate, dispatch, error, loading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 클리어
    if (formErrors[name as keyof AdminLoginFormValues]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<AdminLoginFormValues> = {};
    
    if (!formValues.id.trim()) {
      errors.id = 'ID를 입력해주세요.';
    }
    
    if (!formValues.password.trim()) {
      errors.password = '비밀번호를 입력해주세요.';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoginError('');
      
      // 관리자 로그인 API 호출
      dispatch(adminLogin({ 
        email: formValues.id, 
        password: formValues.password,
        rememberMe: keepLoggedIn
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleKeepLoggedIn = () => {
    setKeepLoggedIn(!keepLoggedIn);
  };

  return (
    <MainContainer>
      <OuterContainer>
        <InnerContainer>
          <FormContainer>
            <Form onSubmit={handleSubmit}>
          {/* 로고 및 제목 */}
          <LogoSection>
            <LogoContainer onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img src="/images/logo-campus-new.svg" alt="TOBE DOCTOR CAMPUS" />
            </LogoContainer>
            <Divider />
            <AdminTitle>ADMIN.</AdminTitle>
          </LogoSection>

          {/* ID 입력 */}
          <FormGroup hasBottomMargin>
            <FormLabel>ID</FormLabel>
            <InputWrapper>
              <Input
                type="text"
                name="id"
                placeholder="ID를 입력해주세요."
                value={formValues.id}
                onChange={handleChange}
                className={formErrors.id ? 'error' : ''}
                autoComplete="username"
              />
            </InputWrapper>
            {formErrors.id && <InputHelp error>{formErrors.id}</InputHelp>}
          </FormGroup>
          
          {/* 비밀번호 입력 */}
          <FormGroup>
            <FormLabel>비밀번호</FormLabel>
            <PasswordInputWrapper>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="비밀번호를 입력해주세요."
                value={formValues.password}
                onChange={handleChange}
                className={formErrors.password ? 'error' : ''}
                autoComplete="current-password"
              />
              <PasswordToggleButton
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                <PasswordEyeIcon />
              </PasswordToggleButton>
            </PasswordInputWrapper>
            {formErrors.password && <InputHelp error>{formErrors.password}</InputHelp>}
            
            {/* 로그인 유지 체크박스 */}
            <CheckboxContainer>
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  id="keepLoggedIn"
                  checked={keepLoggedIn}
                  onChange={toggleKeepLoggedIn}
                />
                <CheckboxLabel htmlFor="keepLoggedIn">
                  <CheckboxIcon checked={keepLoggedIn}>
                    {keepLoggedIn && <LoginCheckIcon />}
                  </CheckboxIcon>
                  로그인 유지
                </CheckboxLabel>
              </CheckboxWrapper>
            </CheckboxContainer>
          </FormGroup>

          {/* 에러 메시지 */}
          {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

          {/* 로그인 버튼 */}
          <LoginButton type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </LoginButton>
            </Form>
          </FormContainer>
        </InnerContainer>
      </OuterContainer>
    </MainContainer>
  );
};

// 스타일 컴포넌트
const MainContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: #F0F7F7;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  display: inline-flex;
`;

const OuterContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  display: flex;
`;

const InnerContainer = styled.div`
  width: 1200px;
  overflow: hidden;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 64px;
  display: flex;
`;

const FormContainer = styled.div`
  width: 608px;
  height: 532px;
  padding: 64px;
  background: white;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 64px;
  display: flex;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const LogoSection = styled.div`
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  display: inline-flex;
  margin-bottom: 64px;
`;

const LogoContainer = styled.div`
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  
  img {
    width: 199px;
    height: 40px;
  }
`;


const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #448181;
`;

const AdminTitle = styled.div`
  color: #448181;
  font-size: 36px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 48.02px;
  word-wrap: break-word;
`;

const FormGroup = styled.div<{ hasBottomMargin?: boolean }>`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
  margin-bottom: 32px;
`;

const FormLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const InputWrapper = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  display: inline-flex;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  transition: border-color 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:focus {
    outline: none;
    border-color: #448181;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  }
  
  &:focus-visible {
    outline: none;
  }
  
  &.error {
    border-color: #ff5252;
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 22px;
    height: 22px;
    fill: rgba(55, 56, 60, 0.61);
  }
  
  &:hover svg {
    fill: rgba(55, 56, 60, 0.8);
  }
`;

const CheckboxContainer = styled.div`
  width: 335px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  display: inline-flex;
  margin-top: 16px;
`;

const CheckboxWrapper = styled.div`
  padding-top: 2px;
  padding-bottom: 2px;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
`;

const Checkbox = styled.input`
  display: none;
`;

const CheckboxLabel = styled.label`
  flex: 1 1 0;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  cursor: pointer;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const CheckboxIcon = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1.5px solid ${props => props.checked ? '#448181' : 'rgba(112, 115, 124, 0.22)'};
  background: ${props => props.checked ? '#448181' : 'transparent'};
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 10px;
    height: 10px;
    fill: white;
  }
`;

const InputHelp = styled.div<{ error?: boolean }>`
  color: ${props => props.error ? '#ff5252' : 'rgba(55, 56, 60, 0.61)'};
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  margin-top: 4px;
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin-bottom: 16px;
`;

const LoginButton = styled.button`
  align-self: stretch;
  padding: 12px 28px;
  background: #448181;
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background: #3a6b6b;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

export default AdminLogin;